/**
 * Condition Evaluator
 * Evaluates condition terms with operators and functions
 */

import { DateUtils } from '../utils/date_utils.js';

export class ConditionEvaluator {
  /**
   * Get field value
   * @param {string} fieldId - Field ID
   * @param {Element} [root] - Optional root to scope the search (e.g. form container)
   * @returns {string}
   */
  static getFieldValue(fieldId, root = null) {
    const scope = root || document;
    const selector = `[name="${fieldId}"], [name="${fieldId}[]"], [id="${fieldId}"]`;
    let field = scope.querySelector(selector);

    // Repeater: names can be fieldId[0], fieldId[1] - match those too
    if (!field && fieldId && scope.querySelector) {
      const withBracket = scope.querySelector(`[name^="${fieldId}["]`);
      if (withBracket) field = withBracket;
    }

    // Ensure we only use a field that is inside root (so repeater row scope is strict)
    if (field && root && !root.contains(field)) {
      field = null;
    }

    if (!field) {
      return '';
    }

    // Handle radio/checkbox
    if (field.type === 'radio' || field.type === 'checkbox') {
      if (field.type === 'checkbox') {
        const checkedBoxes = scope.querySelectorAll(`[name="${fieldId}[]"]:checked, [name="${fieldId}"]:checked`);
        const byBracket = scope.querySelectorAll(`[name^="${fieldId}["]:checked`);
        const combined = checkedBoxes.length ? checkedBoxes : byBracket;
        const values = Array.from(combined).map(cb => cb.value);
        return values.join(',');
      }
      // Radio: exact name or repeater name (fieldId[0], etc.)
      let checked = scope.querySelector(`[name="${fieldId}"]:checked, [name="${fieldId}[]"]:checked`);
      if (!checked) {
        checked = scope.querySelector(`[name^="${fieldId}["]:checked`);
      }
      return checked ? (checked.value || '') : '';
    }

    return field.value || '';
  }

  /**
   * Apply function to field value
   * @param {string} value - Field value
   * @param {string} func - Function name
   * @returns {string|number}
   */
  static applyFunction(value, func) {
    if (!value || !func) return value;
    
    switch (func) {
      case 'YD': // Years difference
        if (!DateUtils.isValidDate(value)) return 0;
        const today = new Date();
        const date = new Date(value);
        return DateUtils.calculateAge(date);
        
      case 'Y': // Year extraction
        if (!DateUtils.isValidDate(value)) return 0;
        return DateUtils.getYear(value);
        
      default:
        return value;
    }
  }

  /**
   * Normalize operator: trim and map aliases / HTML entities to canonical form
   * @param {string} op - Raw operator
   * @returns {string}
   */
  static normalizeOperator(op) {
    if (op == null || typeof op !== 'string') return '=';
    const trimmed = op.trim();
    const aliases = {
      'ne': '!=',
      '&gt;': '>',
      '&gt;=': '>=',
      '&lt;': '<',
      '&lt;=': '<='
    };
    return aliases[trimmed] !== undefined ? aliases[trimmed] : trimmed;
  }

  /**
   * Evaluate condition term
   * @param {Object} term - Condition term
   * @param {Element} [root] - Optional root to scope field value lookup
   * @returns {boolean}
   */
  static evaluateTerm(term, root = null) {
    if (!term || !term.field) {
      return false;
    }

    let fieldValue = this.getFieldValue(term.field, root);
    
    // Apply function if specified
    if (term.function) {
      const originalValue = fieldValue;
      fieldValue = this.applyFunction(fieldValue, term.function);
    }
    
    const operator = this.normalizeOperator(term.operator || '=');
    const expectedValue = term.value;
    const expectedValueNorm = expectedValue == null ? '' : String(expectedValue).trim();
    const fieldValueTrimmed = String(fieldValue).trim();
    const fieldValueLower = fieldValueTrimmed.toLowerCase();
    const expectedLower = expectedValueNorm.toLowerCase();

    let result = false;
    switch (operator) {
      case '=':
        result = fieldValueTrimmed === expectedValueNorm || fieldValueLower === expectedLower;
        break;

      case '!=':
        result = fieldValueTrimmed !== expectedValueNorm && fieldValueLower !== expectedLower;
        break;
        
      case '>':
        result = parseFloat(fieldValue) > parseFloat(expectedValue);
        break;
        
      case '>=':
        result = parseFloat(fieldValue) >= parseFloat(expectedValue);
        break;
        
      case '<':
        result = parseFloat(fieldValue) < parseFloat(expectedValue);
        break;
        
      case '<=':
        result = parseFloat(fieldValue) <= parseFloat(expectedValue);
        break;
        
      case 'null':
        result = fieldValue === null || fieldValue === '' || fieldValue === undefined;
        break;
        
      case 'not null':
        result = fieldValue !== null && fieldValue !== '' && fieldValue !== undefined;
        break;
        
      case 'in':
        if (expectedValueNorm === '' && (expectedValue == null || String(expectedValue).trim() === '')) {
          result = false;
        } else {
          // Split expected values (comma-separated)
          const expectedValues = String(expectedValue ?? '').split(',').map(v => v.trim());
          // Split field value (for checkboxes, this is comma-separated)
          const fieldValues = String(fieldValue).split(',').map(v => v.trim()).filter(v => v !== '');
          
          // Check if any expected value matches any field value
          // This handles both single values and multi-select checkboxes
          result = expectedValues.some(expected => 
            fieldValues.some(fieldVal => fieldVal === expected || fieldVal.indexOf(expected) > -1)
          );
        }
        break;
        
      default:
        result = false;
    }
    
    return result;
  }

  /**
   * Evaluate condition block
   * @param {Object} block - Condition block with terms array
   * @param {Element} [root] - Optional root to scope field value lookup
   * @param {Object} [options] - Optional { controlledFieldId, controlledAreaId } for logging
   * @returns {boolean}
   */
  static evaluateBlock(block, root = null, options = null) {
    if (!block || !block.terms || !Array.isArray(block.terms) || block.terms.length === 0) {
      return false;
    }

    const termResults = block.terms.map(term => this.evaluateTerm(term, root));
    let result = Boolean(termResults[0]);
    // First term does not count for OR/AND; from term 2 onward, combine with previous using that term's connector
    for (let i = 1; i < block.terms.length; i++) {
      const connector = String(block.terms[i].term || '').trim().toUpperCase();
      if (connector === 'OR') {
        result = result || Boolean(termResults[i]);
      } else {
        result = result && Boolean(termResults[i]);
      }
    }
    return result;
  }

  /**
   * Evaluate condition blocks (at least one block must match - OR logic)
   * @param {Array} blocks - Array of condition blocks
   * @param {Element} [root] - Optional root to scope field value lookup (e.g. form container)
   * @param {Object} [options] - Optional { controlledFieldId, controlledAreaId } for logging
   * @returns {boolean}
   */
  static evaluateBlocks(blocks, root = null, options = null) {
    if (!Array.isArray(blocks) || blocks.length === 0) {
      return false;
    }
    const results = blocks.map(block => this.evaluateBlock(block, root, options));
    return results.some(result => result === true);
  }
}

